import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AssignForm_Payload_Type,
  assignForm,
  unassignForm,
  getFormAssignments,
  getFormSubmissions,
  getArchivedSubmissions,
  getAcknowledgementMatrix,
  sendReminder,
  GetAcknowledgementMatrix_Response_Type,
} from '../axiosNetworkRequest/formAssignments';
import { useSelector } from 'react-redux';
import { ReduxRootStateType } from '../../redux/store';
import { track } from '../../utils/MixpanelHelper';
import useNotification from '../../hooks/useNotification';
import { API_Error_Type } from '../axiosClient/axiosClient';

export const useAssignForm = () => {
  const queryClient = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (payload: AssignForm_Payload_Type) => assignForm(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['form-assignments', variables.connection_id] });
      track('File Assigned to User/Role', {
        'File Name':      variables.file_name,
        'Department':     variables.department_name,
        'Due Date':       variables.due_date ?? null,
        'Assigned Via':   variables.assignedViaRoleId ? 'Role' : 'Individual',
        'Assignee Count': variables.user_ids.length,
        'Assignees':      (data?.data ?? []).map(a => ({ Name: `${a.first_name} ${a.last_name}`, Email: a.email_id })),
        'Assigned By':    `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':          userInfo?.email_id,
        'Company Name':   userInfo?.company_name,
      });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to assign form');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useUnassignForm = () => {
  const queryClient = useQueryClient();
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (assignment_id: number) => unassignForm(assignment_id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-assignments'] });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to unassign user');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useGetFormAssignments = (connection_id: number | undefined) =>
  useQuery({
    queryKey: ['form-assignments', connection_id],
    queryFn: () => getFormAssignments(connection_id!),
    enabled: !!connection_id && connection_id > 0,
  });

export const useGetFormSubmissions = (connection_id: number | undefined) =>
  useQuery({
    queryKey: ['form-submissions', connection_id],
    queryFn: () => getFormSubmissions(connection_id!),
    enabled: !!connection_id && connection_id > 0,
    // Presigned URLs expire (~30 min); never reuse stale data across opens.
    staleTime: 0,
  });

export const useGetArchivedSubmissions = (company_id: number | undefined) =>
  useQuery({
    queryKey: ['archived-submissions', company_id],
    queryFn: () => getArchivedSubmissions(company_id!),
    enabled: !!company_id && company_id > 0,
    // Presigned URLs expire (~30 min); never reuse stale data across opens.
    staleTime: 0,
  });

export const useGetAcknowledgementMatrix = (company_id: number) =>
  useQuery({
    queryKey: ['acknowledgement-matrix', company_id],
    queryFn: () => getAcknowledgementMatrix(company_id),
    enabled: !!company_id && company_id > 0,
    refetchInterval: 60_000,
  });

export const useSendReminder = (company_id: number) => {
  const queryClient = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (assignment_id: number) => sendReminder(assignment_id),
    onSuccess: (_, assignment_id) => {
      queryClient.invalidateQueries({ queryKey: ['acknowledgement-matrix'] });
      const matrix = queryClient.getQueryData<GetAcknowledgementMatrix_Response_Type>(['acknowledgement-matrix', company_id]);
      const row    = matrix?.data.find(r => r.assignment_id === assignment_id);
      track('Reminder Sent', {
        'File Name':      row?.file_name ?? 'unknown',
        'Assignee':       row ? `${row.first_name} ${row.last_name}` : 'unknown',
        'Assignee Email': row?.email_id ?? 'unknown',
        'Sent By':        `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':          userInfo?.email_id,
        'Company Name':   userInfo?.company_name,
      });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to send reminder');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};
